import { AlertColor, FormControl, FormControlLabel, FormLabel, InputAdornment, Switch, TextField, Typography } from '@mui/material';
import { Button, Grid, Modal, ModalActions, Spinner, ToastController } from '@platform-ui/design-system';
import React, { useEffect, useState } from 'react';
import Connect from '../../../../Connect/Connect';
import { ZDP_AUTH_SETUP } from '../action_types';
import { useStoreDispatch } from '../../../../Store';

export const OAuthClientCredentials = ({
  contentDisabled = false,
  savedCreds = { externalClientAppId: '', externalClientAppSecret: '', externalClientLoginUrl: '', sandbox: false },
  updateDacoConnection
}: {
  contentDisabled?: boolean;
  savedCreds?: { externalClientAppId: string; externalClientAppSecret: string; externalClientLoginUrl: string; sandbox: boolean };
  updateDacoConnection?: (connectionMethod: string, tenantId: string) => Promise<void>;
}) => {
  const dispatch = useStoreDispatch();
  const [useSandbox, setUseSandbox] = useState(savedCreds.sandbox);
  const [clientAppId, setClientAppId] = useState(savedCreds.externalClientAppId ? '********' : '');
  const [clientAppSecret, setClientAppSecret] = useState(savedCreds.externalClientAppSecret ? '********' : '');
  const [loginUrl, setLoginUrl] = useState(savedCreds.externalClientLoginUrl || 'https://login.salesforce.com');
  const [showClientAppId, setShowClientAppId] = useState(false);
  const [showClientAppSecret, setShowClientAppSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [hasExistingCreds, setHasExistingCreds] = useState(!!savedCreds.externalClientAppId && !!savedCreds.externalClientAppSecret);
  const [toast, setToast] = useState({
    show: false,
    msg: '',
    severity: 'success' as AlertColor
  });
  const isLocal = window.location.host.includes('localhost');
  const dacoProxyPath = `${isLocal ? 'http://localhost:8070' : '/platform/gateway-proxy-v2'}`;
  const connectionsPath = `${dacoProxyPath}/datacon/connections`;

  /**
   * Saves OAuth Client Credentials to DACO and disables OAuth in Billing.
   * Note: This directly calls DACO POST endpoint to save credentials, which updates the connection table.
   * Unlike OAuth/BasicAuth, this does not need to call updateDacoConnection since the DACO connection
   * is updated during the save operation itself.
   */
  const saveClientCredentials = async () => {
    try {
      setSaving(true);
      setToast({ ...toast, show: false });
      const connect: Connect = (window as any).connect;

      // Prevent saving placeholder values when existing credentials are loaded
      if (hasExistingCreds && (clientAppId === '********' || clientAppSecret === '********')) {
        setSaving(false);
        setToast({
          show: true,
          msg: 'Please enter new credentials to update. The existing values are hidden for security.',
          severity: 'error'
        });
        return;
      }

      // Step 1: Retrieve tenant and user IDs from window.connect or cookies
      let zuoraTenantIdFromCookie = '';
      let zuoraUserIdFromCookie = '';

      if (window.connect?.tenant?.tenant_id) {
        zuoraTenantIdFromCookie = window.connect.tenant.tenant_id;
      } else {
        const cookies = document.cookie.split(';').map(c => c.trim());
        cookies.forEach(cookie => {
          const [name, value] = cookie.split('=');
          if (name === 'Zuora-Tenant-Id') {
            zuoraTenantIdFromCookie = value;
          }
          if (name === 'Zuora-User-Id') {
            zuoraUserIdFromCookie = value;
          }
        });
      }

      if (window.connect?.current_user?.user_id) {
        zuoraUserIdFromCookie = window.connect.current_user.user_id;
      }

      const headers = {
        'Content-Type': 'application/json',
        'Zuora-Tenant-Id': zuoraTenantIdFromCookie,
        'Zuora-User-Id': zuoraUserIdFromCookie
      };

      // Step 2: Save credentials to DACO
      const response = await Connect.proxyCall(
        connectionsPath,
        'POST',
        {
          connectionType: 'CAULDRON',
          hostnames: [],
          connectionValues: {
            connectionMethod: 'OAUTH_CLIENT_CREDENTIALS',
            externalClientAppId: clientAppId,
            externalClientAppSecret: clientAppSecret,
            externalClientLoginUrl: loginUrl,
            isSandbox: `${useSandbox}`,
            isEncrypted: true,
            shouldEncrypt: true
          }
        },
        headers
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw Error(err.message || 'Failed to save OAuth Client Credentials');
      }

      await response.json();
      setSaving(false);

      // Reset fields to placeholder after successful save to hide secrets
      setClientAppId('********');
      setClientAppSecret('********');
      setHasExistingCreds(true);

      // Step 3: Update auth state and show success toast
      dispatch({ type: ZDP_AUTH_SETUP, payload: { authSet: true } });
      setToast({
        show: true,
        msg: 'OAuth Client Credentials saved successfully.',
        severity: 'success'
      });
    } catch (err) {
      Connect.log(err);
      setSaving(false);
      setToast({
        show: true,
        msg: err.message || 'Failed to save OAuth Client Credentials. Please try again later!',
        severity: 'error'
      });
    }
  };

  /**
   * Deletes OAuth Client Credentials from DACO.
   */
  const deleteOAuthCreds = async () => {
    try {
      setDeleteLoading(true);
      setToast({ ...toast, show: false });
      const connect: Connect = (window as any).connect;

      // Step 1: Retrieve tenant and user IDs from window.connect or cookies
      let zuoraTenantIdFromCookie = '';
      let zuoraUserIdFromCookie = '';

      if (window.connect?.tenant?.tenant_id) {
        zuoraTenantIdFromCookie = window.connect.tenant.tenant_id;
      } else {
        const cookies = document.cookie.split(';').map(c => c.trim());
        cookies.forEach(cookie => {
          const [name, value] = cookie.split('=');
          if (name === 'Zuora-Tenant-Id') {
            zuoraTenantIdFromCookie = value;
          }
          if (name === 'Zuora-User-Id') {
            zuoraUserIdFromCookie = value;
          }
        });
      }

      if (window.connect?.current_user?.user_id) {
        zuoraUserIdFromCookie = window.connect.current_user.user_id;
      }

      const headers = {
        'Accept': 'application/json',
        'Zuora-Tenant-Id': zuoraTenantIdFromCookie,
        'Zuora-User-Id': zuoraUserIdFromCookie
      };

      // Step 2: Delete credentials from DACO
      const response = await Connect.proxyCall(connectionsPath, 'DELETE', undefined, headers);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw Error(err.message || response.statusText);
      }

      // Step 3: Close modal, update auth state, and show success toast
      setDeleteLoading(false);
      setOpenDeleteConfirm(false);
      dispatch({ type: ZDP_AUTH_SETUP, payload: { authSet: false } });
      setToast({
        show: true,
        msg: 'OAuth Client Credentials were deleted successfully.',
        severity: 'success'
      });
    } catch (err) {
      Connect.log(err);
      setDeleteLoading(false);
      setOpenDeleteConfirm(false);
      setToast({
        show: true,
        msg: err.message || 'Failed to delete OAuth Client Credentials. Please try again later!',
        severity: 'error'
      });
    }
  };

  useEffect(() => {
    if (window.location.search) {
      setToast({ ...toast, show: false });
      const params = new URLSearchParams(window.location.search);
      const oauthMessage = params.get('oauthMessage');
      if (oauthMessage) {
        setToast({
          show: true,
          msg: oauthMessage,
          severity: 'success'
        });
      }
    }
  }, [window.location.search]);

  return (
    <>
      {
        saving ?
        <Spinner /> :
        <Grid container direction='column'>
          { toast.show && <ToastController severity={toast.severity} message={toast.msg} /> }
          <Grid item xs={6}>
            <TextField
              fullWidth
              label='External Client App ID'
              value={clientAppId}
              onChange={(e) => setClientAppId(e.target.value)}
              placeholder='Enter Salesforce Connected App Client ID'
              disabled={contentDisabled}
              type={showClientAppId ? 'text' : 'password'}
              InputProps={{
                endAdornment:
                  <InputAdornment position='end'>
                    <Button
                      icon={showClientAppId ? 'visibility_off' : 'visibility'}
                      dsOnClick={() => setShowClientAppId(!showClientAppId)}
                      disabled={contentDisabled}
                    />
                  </InputAdornment>
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label='External Client App Secret'
              value={clientAppSecret}
              onChange={(e) => setClientAppSecret(e.target.value)}
              placeholder='Enter Salesforce Connected App Client Secret'
              disabled={contentDisabled}
              type={showClientAppSecret ? 'text' : 'password'}
              InputProps={{
                endAdornment:
                  <InputAdornment position='end'>
                    <Button
                      icon={showClientAppSecret ? 'visibility_off' : 'visibility'}
                      dsOnClick={() => setShowClientAppSecret(!showClientAppSecret)}
                      disabled={contentDisabled}
                    />
                  </InputAdornment>
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label='Login URL'
              value={loginUrl}
              onChange={(e) => setLoginUrl(e.target.value)}
              placeholder='https://login.salesforce.com'
              disabled={contentDisabled}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl component='fieldset' variant='standard' disabled={contentDisabled}>
              <FormLabel>Use Sandbox</FormLabel>
              <FormControlLabel
                control={
                  <Switch
                    checked={useSandbox}
                    onChange={(_, checked) => setUseSandbox(checked)}
                    disabled={contentDisabled} />
                }
                label=''
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <Grid container item sx={{ marginTop: '10px' }} alignContent='flex-end' alignItems='center' justify='flex-end'>
              <Button
                disabled={!clientAppId || !clientAppSecret || saving || (hasExistingCreds && (clientAppId === '********' || clientAppSecret === '********'))}
                dsOnClick={saveClientCredentials}
              >
                { saving ? 'Saving...' : 'Save' }
              </Button>
              <Button
                variant='outlined'
                disabled={saving}
                dsOnClick={() => setOpenDeleteConfirm(true)}
                sx={{ marginLeft: '10px' }}
              >
                Delete
              </Button>
            </Grid>
          </Grid>
        </Grid>
      }
      <Modal
        id='delete-client-creds-confirm-modal'
        open={openDeleteConfirm}
        dsOnClose={() => setOpenDeleteConfirm(false)}
        disableBackdropClick
        body={<Typography variant='h5'>Are you sure you want to delete your OAuth Client Credentials? This will stop the sync.</Typography>}
        footer={
          <ModalActions>
            <Button
              variant='outlined'
              disabled={deleteLoading}
              dsOnClick={() => setOpenDeleteConfirm(false)}
            >
              No
            </Button>
            <Button
              disabled={deleteLoading}
              dsOnClick={deleteOAuthCreds}
            >
              { deleteLoading ? 'Deleting...' : 'Yes' }
            </Button>
          </ModalActions>
        }
      />
    </>
  );
};
